import { createContext, useContext, useMemo, useEffect, useState } from 'react';
import once from 'transmute/once';
import getIn from 'transmute/getIn';
import Raven from 'raven-js';
import memoize from 'transmute/memoize';
import { getHasAppSettingsOption, getAppSettingsUIModes, useFetchFrameworkAppSettings } from 'framework-listing-lib/internal/frameworkAppSettings/hooks/useFrameworkAppSettings';
import useFetchListingLibData from '../hooks/useFetchListingLibData';
import { clearQuickFetchErrorsFromWindow, getQuickFetchErrorsFromWindow } from '../../earlyRequester/utils';
import useApplicationId from '../hooks/useApplicationId';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { LISTING_LIB_APP_SETTING_NAMESPACE } from '../frameworkAppSettings/constants';
import { jsx as _jsx } from "react/jsx-runtime";
const DataContext = /*#__PURE__*/createContext(undefined);
const trackErrors = once(() => {
  // Track any errors we encountered during the quick-fetches, except timeouts as we can't fix those.
  getQuickFetchErrorsFromWindow().forEach(error => {
    const status = getIn(['xhttp', 'status'], error);
    if (status === 0) {
      return;
    }
    Raven.captureException(error, {
      tags: {
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'boolean' is not assignable to type 'string'.
        fromQuickFetch: true
      },
      extra: {
        response: getIn(['xhttp', 'response'], error),
        responseText: getIn(['xhttp', 'responseText'], error),
        responseURL: getIn(['xhttp', 'responseURL'], error),
        status
      }
    });
  });
  clearQuickFetchErrorsFromWindow();
});
export default function DataProvider({
  children,
  showHighlySensitiveProperties
}) {
  const {
    isUngatedFor,
    hasScope
  } = useUserInfo();
  const applicationId = useApplicationId();
  const [globalError, setGlobalError] = useState({});
  const {
    data: appSettings,
    loading: loadingAppSettings
  } = useFetchFrameworkAppSettings();
  const memoizedGetHasAppSettingsOption = useMemo(() => {
    function memoizedFn(optionName) {
      var _appSettings$LISTING_;
      return getHasAppSettingsOption(optionName, {
        isUngatedFor,
        hasScope,
        applicationId,
        appSettings: appSettings === null || appSettings === void 0 || (_appSettings$LISTING_ = appSettings[LISTING_LIB_APP_SETTING_NAMESPACE]) === null || _appSettings$LISTING_ === void 0 ? void 0 : _appSettings$LISTING_.metadata
      });
    }
    return memoize(memoizedFn);
  }, [appSettings, applicationId, hasScope, isUngatedFor]);
  const uiModes = useMemo(() => {
    var _appSettings$LISTING_2;
    return getAppSettingsUIModes({
      isUngatedFor,
      hasScope,
      applicationId,
      appSettings: appSettings === null || appSettings === void 0 || (_appSettings$LISTING_2 = appSettings[LISTING_LIB_APP_SETTING_NAMESPACE]) === null || _appSettings$LISTING_2 === void 0 ? void 0 : _appSettings$LISTING_2.metadata
    });
  }, [appSettings, applicationId, hasScope, isUngatedFor]);
  const {
    loading: loadingListingLibData,
    properties,
    propertyGroups,
    typeDefinitions
  } = useFetchListingLibData({
    showHighlySensitiveProperties
  });
  const loading = useMemo(() => loadingAppSettings || loadingListingLibData, [loadingAppSettings, loadingListingLibData]);
  useEffect(() => {
    if (!loading) {
      trackErrors();
    }
  }, [loading]);
  const value = useMemo(() => {
    return {
      getHasAppSettingsOption: memoizedGetHasAppSettingsOption,
      globalError,
      loading,
      properties,
      propertyGroups,
      setGlobalError,
      typeDefinitions,
      uiModes
    };
  }, [globalError, loading, properties, propertyGroups, typeDefinitions, memoizedGetHasAppSettingsOption, uiModes]);
  return /*#__PURE__*/_jsx(DataContext.Provider, {
    value: value,
    children: children
  });
}
export function useListingLibData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useListingLibData must be used within a DataProvider');
  }
  return context;
}
export function useGlobalError() {
  const {
    globalError,
    setGlobalError
  } = useListingLibData();
  return [globalError, setGlobalError];
}